import result from './aws/result'
import AWS from 'aws-sdk'

const REGION = 'ap-northeast-1'

/**
 * AWSの資格情報を設定する.
 * @param {{access_key_id: string, secret_key: string, session_token: string}} credentials 資格情報
 */
const configureAws = (credentials) => {
  const cred = new AWS.Credentials(
    credentials.access_key_id,
    credentials.secret_key,
    credentials.session_token
  )
  AWS.config.update({
    credentials: cred,
    region: REGION,
    computeChecksums: true,
  })
}

/**
 * s3接続処理.
 * @param {{state: object, dispatch: function, commit: function}} context 
 * @param {{cloud_construction_id: string}} args 
 * @param {(*) => {}} callback 
 */
const s3Proc = async (context, cloudConstuctionId, apiOptions, callback) => {
  try {
    if( !apiOptions.noDisplayLoading ){
      context.dispatch("view/start", null, { root: true });
    }
    if (!cloudConstuctionId) {
      throw '工事ID指定がない'
    }

    const s3InfoMap = context.state.s3InfoMap

    if(s3InfoMap[cloudConstuctionId]){
      const s3Info = s3InfoMap[cloudConstuctionId]
      configureAws(s3Info.credentials)
      try {
        const s3 = new AWS.S3({
          apiVersion: "2006-03-01",
          params: { Bucket: s3Info.s3.bucket }
        })
        // 有効期限のチェックのため,ルートフォルダの情報を取る(取得結果をキャッシュしない.)
        const rootFolder = await s3.getObject({
          Bucket: s3Info.s3.bucket,
          Key: s3Info.s3.key,
          ResponseCacheControl: 'private, no-store, max-age=0'
        }).promise()
        console.log(rootFolder)
        return await callback(s3Info)
      } catch(err) {
        console.log(err)
        if(err.name !== 'ExpiredToken'){
          // 有効期限切れ以外のエラーは、投げる
          throw err
        }
      }
    }
    // 接続情報がない, もしくは有効期限切れの場合に、新規取得する
    const s3Info = await context.dispatch("api/getCloudConstructionS3Info", 
      {
        cloud_construction_id: cloudConstuctionId,
        __apiOptions: { noDisplayLoading: true, noErrorDialog: true }
      },
      { root: true }
    );
    s3InfoMap[cloudConstuctionId] = s3Info
    context.commit('updateS3InfoMap', s3InfoMap)
    configureAws(s3Info.credentials)

    return await callback(s3Info)
  } catch(e) {
    context.commit('api/setApiError', { error: e }, { root: true })
    console.log('system error caught:', e)
  } finally {
    if( !apiOptions.noDisplayLoading ){
      context.dispatch("view/end", null, { root: true });
    }
  }
}

export default {
  namespaced: true,
  modules: {
    result,
  },
  state: {
    // キー:工事ID, 値:s3接続情報
    s3InfoMap: {}
  },
  mutations: {
    updateS3InfoMap: (state, payload) => (state.s3InfoMap = payload)
  },
  actions: {
    /**
     * 指定工事のファイルリストを返す.
     * @param {{
     *   state: object, dispatch: function, commit: function
     * }} context 
     * @param {{
     *   cloud_construction_id: string,
     *   folder_path: string,
     *   url: boolean,
     *   add_self_key: boolean,
     *   __apiOptions: { noDisplayLoading: boolean, noErrorDialog: boolean }
     * }} args
     */
    async fileListOf(context, args) {
      const apiOptions = args.__apiOptions || {}

      return s3Proc(context, args.cloud_construction_id, apiOptions, async (s3Info) => {
        const s3 = new AWS.S3({
          apiVersion: "2006-03-01",
          params: { Bucket: s3Info.s3.bucket }
        })

        const prefix = s3Info.s3.key + args.folder_path

        const data = await s3.listObjects({ Prefix: prefix, Delimiter: '/' }) .promise()
      
        const fileList = await Promise.all(data.Contents
          .filter((f) => {
            if(args.add_self_key) return true;
            return (prefix !== f.Key)
          })
          .map(async (f) => {
            const name = f.Key.replace(prefix, '')
            console.log(f)
        
            const fileInfo = {
              name,
              size: f.Size,
              lastModified: f.LastModified,
            }
            if(args.url){
              fileInfo.url = await s3.getSignedUrlPromise('getObject', {
                Bucket: s3Info.s3.bucket,
                Key: f.Key
              })
            }
            return fileInfo
          })
        );
        const folderList = data.CommonPrefixes.map((folder) => {
          return folder.Prefix
        })

        const folderInfo = {
          cloud_construction_id: args.cloud_construction_id,
          file_list: fileList,
          folder_list: folderList
        }
        context.commit('result/putUserdataFolderInfo',folderInfo);
        return folderInfo
      })
    },
    /**
     * 指定工事のファイルをアップロードする.
     * @param {{state: object, dispatch: function, commit: function}} context 
     * @param {{
     *  cloud_construction_id: string,
     *  file_path: string,
     *  file: object,
     *   __apiOptions: { noDisplayLoading: boolean, noErrorDialog: boolean }
     * }} args 
     */
    async uploadTo(context, args) {
      const apiOptions = args.__apiOptions || {}

      return s3Proc(context, args.cloud_construction_id, apiOptions, async (s3Info) => {
        const key = s3Info.s3.key + args.file_path

        return new AWS.S3.ManagedUpload({
          params: {
            Bucket: s3Info.s3.bucket,
            Key: key,
            Body: args.file,
          }
        }).promise();
      })
    },
    /**
     * 指定工事の黒板データリストを返す.
     * @param {{
     *   state: object, dispatch: function, commit: function
     * }} context 
     * @param {{
     *   cloud_construction_id: string,
     *   kokubandata_list: {
     *     created_key: string,
     *     kokuban_info_json: string,
     *     organize_info_json: string,
     *     send_app_list: { app_id: string, app_name: string }[]
     *     thumbnail_s3path: string,
     *     created_at: number,
     *   }[],
     *   __apiOptions: { noDisplayLoading: boolean, noErrorDialog: boolean }
     * }} args
     * @returns {{
     *   cloud_construction_id: string,
     *   kokuban_data_list: {
     *     kokuban_id: string,
     *     image_url: string,
     *     kokuban_info: object,
     *     organize_info: object,
     *     send_app_list: { app_id: string, app_name: string }[]
     *     created_at: number,
     *   }[],
     * }} 黒板データリスト
     */
    async kokubanDataListOf(context, args) {
      const apiOptions = args.__apiOptions || {}
 
      return s3Proc(context, args.cloud_construction_id, apiOptions, async (s3Info) => {
        const s3 = new AWS.S3({
          apiVersion: "2006-03-01",
          params: { Bucket: s3Info.s3.bucket }
        })
        const kokubanDataList = await Promise.all(args.kokubandata_list
          .map(async (kokuban) => {
            const url = await s3.getSignedUrlPromise('getObject', {
              Bucket: s3Info.s3.bucket,
              Key: kokuban.thumbnail_s3path
            })
            const kokubanRec = {
              kokuban_id: kokuban.created_key,
              image_url: url,
              kokuban_info: JSON.parse(kokuban.kokuban_info_json),
              send_app_list: kokuban.send_app_list,
              created_at: kokuban.created_at,
            }
            if (kokuban.organize_info_json) {
              kokubanRec.organize_info = JSON.parse(kokuban.organize_info_json)
            }
            return kokubanRec
          })
        );

        const kokubanData = {
          cloud_construction_id: args.cloud_construction_id,
          kokuban_data_list: kokubanDataList
        }
        context.commit('result/putKokubanData', kokubanData);
        return kokubanData
      })
    },
  },
}
